import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  Block,
  CheckCircleOutline,
  Dangerous,
  Gavel,
  InfoOutlined,
  ReceiptLongOutlined,
  TimerOutlined,
  AccountBalanceWalletOutlined
} from '@mui/icons-material';
import {ShieldAlert} from 'lucide-react';
import BaseModal from '../../../utils/BaseModal';
import api from '../../../../api/axios';
import {Trade, TradeStatus, TradeGroup, ViewMode} from '../../../../types/trades';
import {formatMoney, moneyFromMinor} from '../../../utils/UnitConversion.utils';
import {useTranslation} from 'react-i18next';
import {RootState, useAppDispatch} from '../../../../store';
import {fetchTradeGroups} from '../../../../store/slices/tradeGroupsSlice';

export interface UserRestrictionDTO {
  activeRestrictionPoints: number;
  excellentCandidate: boolean;
  excellentProtectionRemainingPoints: number;
}

type StandingLevel = 'EXCELLENT' | 'GOOD' | 'POOR' | 'AT_RISK' | 'BANNED';

type Props = {
  open: boolean;
  trades: Trade[];
  mode: ViewMode;
  restrictions: UserRestrictionDTO | null;
  onClose: () => void;
  onSuccess: (
      tradeId: string,
      newStatus: TradeStatus,
      localDate: Date,
      groupUpdates?: {
        tradeGroupId: string;
        tradeGroupLocked: boolean;
      }
  ) => void;
};

const CancelTradeModal: React.FC<Props> = ({open, trades, mode, restrictions, onClose, onSuccess}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const isSeller = mode === 'sold';

  const currencyRate = useSelector((state: RootState) => state.currency.rate);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);
  const walletBalance = useSelector((state: RootState) => state.auth.wallet?.walletTotalAmountMinor) ?? 0;

  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (!open) return;

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);

    return () => clearInterval(timer);
  }, [open]);

  const tradeInfo = useMemo(() => {
    if (!trades || trades.length === 0) return null;

    const convert = (minorValue: number) => {
      const baseValue = moneyFromMinor(minorValue);
      return formatMoney(baseValue * currencyRate);
    };

    const calculateDistribution = (totalPenaltyMinor: number) => {
      const hasEnoughFunds = walletBalance >= totalPenaltyMinor;

      if (totalPenaltyMinor < 100) {
        return {
          toOpponent: convert(0),
          toSystem: convert(totalPenaltyMinor),
          isPending: !hasEnoughFunds,
          splitMode: 'system_only'
        };
      }

      const half = Math.floor(totalPenaltyMinor / 2);

      return {
        toOpponent: convert(half),
        toSystem: convert(totalPenaltyMinor - half),
        isPending: !hasEnoughFunds,
        splitMode: 'split_50_50'
      };
    };

    let totalItemValueMinor = 0;
    let totalPenaltyMinor = 0;
    let totalFeeMinor = 0;
    let totalRefundMinor = 0;
    let allFree = true;
    let maxTimeLeftMs = 0;

    trades.forEach(trade => {
      totalItemValueMinor += trade.priceMinor;

      if (!isSeller) {
        const createdDate = new Date(trade.creationDate);
        const diffMs = currentTime.getTime() - createdDate.getTime();
        const ninetyMinMs = 90 * 60 * 1000;
        const isFree = diffMs >= ninetyMinMs;

        if (!isFree) {
          allFree = false;
          maxTimeLeftMs = Math.max(maxTimeLeftMs, ninetyMinMs - diffMs);
        }

        const currentFeeMinor = isFree ? 0 : Math.ceil(trade.priceMinor * 0.02);
        totalFeeMinor += currentFeeMinor;
        totalRefundMinor += trade.priceMinor - currentFeeMinor;
      }
    });

    if (!isSeller) {
      const distribution = calculateDistribution(totalFeeMinor);

      return {
        isFree: allFree,
        timeLeftMs: maxTimeLeftMs,
        total: convert(totalItemValueMinor),
        fee: convert(totalFeeMinor),
        resultAmount: convert(totalRefundMinor),
        distribution,
        currentLevel: 'GOOD' as StandingLevel,
        nextLevel: 'GOOD' as StandingLevel,
        nextPenaltyKey: '',
        isPardonUsed: false,
        colorClass: '',
        percent: '2',
        itemValue: '0',
        penalty: '0'
      };
    }

    const activeRestrictionPoints = restrictions?.activeRestrictionPoints ?? 0;
    const excellentCandidate = restrictions?.excellentCandidate ?? false;
    const excellentProtectionRemainingPoints = restrictions?.excellentProtectionRemainingPoints ?? 0;

    let penaltyPercent = 0.04;
    let currentLevel: StandingLevel = 'GOOD';
    let nextLevel: StandingLevel = 'POOR';
    let nextPenaltyKey = 'penalty_1day';
    let isPardonUsed = false;
    let colorClass = 'text-blue-500';

    if (activeRestrictionPoints >= 300) {
      currentLevel = 'BANNED';
      nextLevel = 'BANNED';
      penaltyPercent = 0.10;
      nextPenaltyKey = 'penalty_is_banned';
      colorClass = 'text-red-600';
    } else if (activeRestrictionPoints >= 200) {
      currentLevel = 'AT_RISK';
      nextLevel = 'BANNED';
      penaltyPercent = 0.10;
      nextPenaltyKey = 'penalty_ban';
      colorClass = 'text-orange-600';
    } else if (activeRestrictionPoints >= 100) {
      currentLevel = 'POOR';
      nextLevel = 'AT_RISK';
      penaltyPercent = 0.06;
      nextPenaltyKey = 'penalty_ban';
      colorClass = 'text-amber-500';
    } else if (
        activeRestrictionPoints === 0 &&
        excellentCandidate &&
        excellentProtectionRemainingPoints > 0
    ) {
      currentLevel = 'EXCELLENT';
      nextLevel = 'GOOD';
      penaltyPercent = 0;
      nextPenaltyKey = 'penalty_pardon';
      isPardonUsed = true;
      colorClass = 'text-emerald-500';
    }

    totalPenaltyMinor = Math.ceil(totalItemValueMinor * penaltyPercent);

    const distribution = calculateDistribution(totalPenaltyMinor);

    return {
      penalty: penaltyPercent === 0 ? '0.00' : convert(totalPenaltyMinor),
      percent: (penaltyPercent * 100).toFixed(0),
      itemValue: convert(totalItemValueMinor),
      currentLevel,
      nextLevel,
      nextPenaltyKey,
      isPardonUsed,
      colorClass,
      distribution,
      isFree: false,
      timeLeftMs: 0,
      total: '0',
      fee: '0',
      resultAmount: '0'
    };
  }, [trades, currentTime, currencyRate, isSeller, restrictions, walletBalance]);

  if (!trades || !tradeInfo || trades.length === 0) return null;

  const handleCancel = async () => {
    setLoading(true);

    const tradeIds = trades.map(trade => trade.id);

    try {
      const response = await api.post<TradeGroup>('/user/trade/cancel', {tradeIds, mode});

      const tradeGroup = response.data;
      const returnedGroupId = String(tradeGroup.id);

      dispatch(fetchTradeGroups([returnedGroupId]));

      trades.forEach(trade => {
        const nextStatus: TradeStatus =
            isSeller && trade.effectiveTradeStatus === 'WAITING_FOR_SELLER_SEND'
                ? 'WAITING_FOR_SELLER_SEND_CANCELLED'
                : 'WAITING_FOR_SELLER_CONFIRM_CANCELLED';

        onSuccess(trade.id, nextStatus, new Date(), {
          tradeGroupId: returnedGroupId,
          tradeGroupLocked: true
        });
      });

      onClose();
    } catch (error) {
      console.error('Batch cancel failed', error);
    } finally {
      setLoading(false);
    }
  };

  const distributionSection = (
      <div className="mt-4 pt-3 border-t border-white/5 space-y-2">
        <div className="flex items-center gap-2 mb-1">
          <AccountBalanceWalletOutlined className="text-gray-500 !w-3.5"/>
          <span className="text-[10px] uppercase tracking-tighter font-bold text-gray-400">
            {t('history.actions.fee_distribution')}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white/5 p-2 rounded border border-white/5">
            <p className="text-[9px] text-gray-500 uppercase font-bold">
              {isSeller ? t('roles.buyer') : t('history.roles.seller')}
            </p>
            <p className="text-xs font-mono text-gray-200">
              {currencySymbol}{tradeInfo.distribution.toOpponent}
            </p>
          </div>

          <div className="bg-white/5 p-2 rounded border border-white/5">
            <p className="text-[9px] text-gray-500 uppercase font-bold">
              {t('history.roles.moonskins')}
            </p>
            <p className="text-xs font-mono text-gray-200">
              {currencySymbol}{tradeInfo.distribution.toSystem}
            </p>
          </div>
        </div>

        {tradeInfo.distribution.isPending && (
            <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded">
              <ShieldAlert size={12} className="text-amber-500 shrink-0"/>
              <p className="text-[9px] text-amber-200/80 leading-tight italic">
                {t('history.actions.seller_cancel_confirmation_desc')}
              </p>
            </div>
        )}
      </div>
  );

  const sellerContent = (
      <div className="space-y-5">
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${tradeInfo.isPardonUsed ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
          {tradeInfo.isPardonUsed ? (
              <CheckCircleOutline className="text-emerald-500 mt-0.5" fontSize="small"/>
          ) : (
              <Dangerous className="text-red-500 mt-0.5" fontSize="small"/>
          )}

          <div className="flex flex-col">
            <p className={`text-sm font-bold uppercase tracking-wide ${tradeInfo.isPardonUsed ? 'text-emerald-200' : 'text-red-200'}`}>
              {tradeInfo.isPardonUsed
                  ? t('history.standing.penalty_pardon')
                  : t('history.actions.penalty_warning_title')}
            </p>

            <p className={`text-[11px] leading-tight mt-1 ${tradeInfo.isPardonUsed ? 'text-emerald-200/70' : 'text-red-200/70'}`}>
              {tradeInfo.isPardonUsed
                  ? t('history.actions.excellent_pardon_desc')
                  : t('history.actions.seller_cancel_notice')}
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-white/5 bg-neutral-900/40 overflow-hidden shadow-hidden">
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 border-b border-white/5">
            <Gavel className="text-gray-500 !w-4"/>
            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">
              {t('history.actions.penalty_summary')} ({trades.length} {t('trades')})
            </span>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">{t('history.actions.item_value')}</span>
              <span className="text-gray-200 font-mono">
                {currencySymbol}{tradeInfo.itemValue}
              </span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className={tradeInfo.isPardonUsed ? 'text-emerald-400/80' : 'text-red-400/80'}>
                {t('history.actions.penalty_fee')} ({tradeInfo.percent}%)
              </span>

              <span className={`font-mono font-bold ${tradeInfo.isPardonUsed ? 'text-emerald-400' : 'text-red-400'}`}>
                {tradeInfo.isPardonUsed
                    ? t('history.fees.free')
                    : `${currencySymbol}${tradeInfo.penalty}`}
              </span>
            </div>

            {!tradeInfo.isPardonUsed && distributionSection}

            <div className="pt-3 mt-1 border-t border-white/5">
              <div className="flex justify-between items-end">
                <div className="flex flex-col">
                  <span className="text-gray-200 font-bold text-[11px] uppercase tracking-tight">
                    {t('history.actions.account_standing_impact')}
                  </span>

                  <div className="flex items-center gap-1.5 mt-2">
                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border border-white/5 bg-white/5 ${tradeInfo.colorClass}`}>
                      {t(`dashboard.profile.standing.${tradeInfo.currentLevel?.toLowerCase() || 'good'}`)}
                    </span>

                    <span className="text-gray-600 text-xs">→</span>

                    <span className="text-orange-500 text-[10px] font-black uppercase bg-orange-500/10 px-1.5 py-0.5 rounded border border-orange-500/20">
                      {t(`dashboard.profile.standing.${tradeInfo.nextLevel?.toLowerCase() || 'poor'}`)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col items-end">
                  <div className={`flex items-center gap-1 ${tradeInfo.nextLevel === 'BANNED' ? 'text-red-600' : 'text-orange-500'}`}>
                    {tradeInfo.nextLevel === 'BANNED'
                        ? <Block sx={{fontSize: 14}}/>
                        : <ShieldAlert size={14}/>}
                    <span className="text-[10px] font-black uppercase tracking-tighter">
                      {t(`history.standing.${tradeInfo.nextPenaltyKey || 'penalty_1day'}`)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );

  const buyerContent = (
      <div className="space-y-5">
        {tradeInfo.isFree ? (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <InfoOutlined className="text-emerald-500" fontSize="small"/>
              <p className="text-sm text-emerald-200 font-medium">
                {t('history.actions.free_cancellation_available')}
              </p>
            </div>
        ) : (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <TimerOutlined className="text-orange-500" fontSize="small"/>

              <div className="flex flex-col">
                <p className="text-sm text-orange-200 font-bold">
                  {Math.floor(tradeInfo.timeLeftMs! / 60000)}m {Math.floor((tradeInfo.timeLeftMs! % 60000) / 1000)}s {t('history.actions.remaining')}
                </p>

                <p className="text-[11px] text-orange-200/60 leading-tight">
                  {t('history.actions.cancel_fee_notice')}
                </p>
              </div>
            </div>
        )}

        <div className="rounded-xl border border-white/5 bg-neutral-900/40 overflow-hidden shadow-inner">
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 border-b border-white/5">
            <ReceiptLongOutlined className="text-gray-500 !w-4"/>
            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">
              {t('history.actions.refund_summary')} ({trades.length} {t('trades')})
            </span>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">{t('history.actions.item_price')}</span>
              <span className="text-gray-200 font-mono">
                {currencySymbol}{tradeInfo.total}
              </span>
            </div>

            {!tradeInfo.isFree && (
                <>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-red-400/80">
                      {t('history.actions.cancellation_fee')} (2%)
                    </span>
                    <span className="text-red-400 font-mono font-bold">
                      - {currencySymbol}{tradeInfo.fee}
                    </span>
                  </div>

                  {distributionSection}
                </>
            )}

            <div className="pt-2 mt-2 border-t border-white/5 flex justify-between items-center">
              <span className="text-gray-200 font-bold">
                {t('history.actions.total_refund')}
              </span>
              <span className="text-emerald-400 font-mono text-lg font-extrabold">
                {currencySymbol}{tradeInfo.resultAmount}
              </span>
            </div>
          </div>
        </div>
      </div>
  );

  return (
      <BaseModal
          open={open}
          title={isSeller ? t('history.actions.cancel_sale_title') : t('history.actions.cancel_trade_title')}
          message={isSeller ? sellerContent : buyerContent}
          abortText={t('history.actions.back')}
          submitText={loading ? t('history.actions.processing') : t('history.actions.confirm_cancel')}
          submitColor="error"
          onAbort={onClose}
          onSubmit={handleCancel}
          maxWidth="xs"
      />
  );
};

export default CancelTradeModal;